import React, { useCallback, useRef, useState } from 'react';
// Blueprint removed — using Skyline buttons + spinners
import { supabaseClient } from '../../../utils/supabase';
import { getSignedUrl } from '../../../utils/fileUtils';
import { toast as message } from 'sonner';
import type { FilePointer } from '../../../types/forms';

const MAX_FILE_BYTES = 100 * 1024 * 1024; // 100 MB — must match edge function limit

interface FileFieldInputProps {
  fieldName: string;
  schemaName: string;
  tableName: string;
  recordId: string | number | null;
  value: FilePointer[];
  allowMultiple: boolean;
  allowedMimeTypes: string[];
  isReadOnly: boolean;
  onUpdate: (newValue: FilePointer[]) => void;
}

const FileFieldInput: React.FC<FileFieldInputProps> = ({
  fieldName,
  schemaName,
  tableName,
  recordId,
  value,
  allowMultiple,
  allowedMimeTypes,
  isReadOnly,
  onUpdate,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  // Cache signed URLs so right-click "Save link as" works without waiting
  const [signedUrls, setSignedUrls] = useState<Record<number, string>>({});
  const [loadingUrlId, setLoadingUrlId] = useState<number | null>(null);

  const noRecord = !recordId;

  const uploadFiles = async (files: File[]) => {
    if (!files.length) return;

    if (!allowMultiple) {
      if (value.length > 0) {
        message.warning('Single-file field — remove the existing file first');
        return;
      }
      if (files.length > 1) {
        message.warning('This field only accepts a single file');
        return;
      }
    }

    if (allowedMimeTypes.length > 0) {
      const rejected = files.filter((f) => !allowedMimeTypes.includes(f.type));
      if (rejected.length > 0) {
        message.error(
          `File type not allowed: ${rejected.map((f) => f.name).join(', ')}`,
        );
        return;
      }
    }

    const oversized = files.filter((f) => f.size > MAX_FILE_BYTES);
    if (oversized.length > 0) {
      message.error(
        `File(s) exceed the ${MAX_FILE_BYTES / 1024 / 1024} MB limit: ${oversized.map((f) => f.name).join(', ')}`,
      );
      return;
    }

    setUploading(true);
    const newPointers: FilePointer[] = [];

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('schema_name', schemaName);
      formData.append('table_name', tableName);
      formData.append('record_id', String(recordId));
      formData.append('field_name', fieldName);

      try {
        const { data, error } = await supabaseClient.functions.invoke('file-upload', {
          body: formData,
        });

        if (error) {
          message.error(`Failed to upload ${file.name}: ${error.message}`);
          continue;
        }

        newPointers.push(data as FilePointer);
      } catch {
        message.error(`Upload failed for ${file.name}`);
      }
    }

    setUploading(false);

    if (newPointers.length > 0) {
      onUpdate([...value, ...newPointers]);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (inputRef.current) inputRef.current.value = '';
    await uploadFiles(files);
  };

  // ─── Drag & drop ──────────────────────────────────────────────────────
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    if (isReadOnly || noRecord) return;

    const files = Array.from(e.dataTransfer.files);
    if (!allowMultiple && value.length > 0) {
      message.warning('Single-file field — remove the existing file first');
      return;
    }
    if (!allowMultiple && files.length > 1) {
      message.warning('This field only accepts a single file');
      return;
    }
    await uploadFiles(files);
  }, [isReadOnly, noRecord, allowMultiple, value, schemaName, tableName, recordId, fieldName]);

  // ─── Signed URL on hover (for right-click download) ───────────────────
  const prefetchSignedUrl = useCallback(async (pointer: FilePointer) => {
    if (signedUrls[pointer.id]) return; // already cached
    setLoadingUrlId(pointer.id);
    const url = await getSignedUrl(pointer.id);
    if (url) {
      setSignedUrls((prev) => ({ ...prev, [pointer.id]: url }));
    }
    setLoadingUrlId(null);
  }, [signedUrls]);

  const handleFileClick = (pointer: FilePointer) => {
    const cached = signedUrls[pointer.id];
    if (cached) {
      window.open(cached, '_blank', 'noopener,noreferrer');
    } else {
      // Fallback: fetch on click
      getSignedUrl(pointer.id).then((url) => {
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
        else message.error(`Could not open ${pointer.filename}`);
      });
    }
  };

  const handleDelete = async (pointer: FilePointer) => {
    setDeletingId(pointer.id);
    try {
      const { error } = await supabaseClient.functions.invoke('file-delete', {
        body: {
          file_object_id: pointer.id,
          schema_name: schemaName,
          table_name: tableName,
          record_id: String(recordId),
          field_name: fieldName,
        },
      });

      if (error) {
        message.error(`Failed to delete ${pointer.filename}: ${error.message}`);
        return;
      }

      // Clear cached URL
      setSignedUrls((prev) => {
        const next = { ...prev };
        delete next[pointer.id];
        return next;
      });
      onUpdate(value.filter((f) => f.id !== pointer.id));
    } catch {
      message.error(`Delete failed for ${pointer.filename}`);
    } finally {
      setDeletingId(null);
    }
  };

  const canUpload = !allowMultiple ? value.length === 0 : true;

  return (
    <div
      onDragOver={canUpload && !isReadOnly && !noRecord ? handleDragOver : undefined}
      onDragLeave={handleDragLeave}
      onDrop={canUpload && !isReadOnly && !noRecord ? handleDrop : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: dragging ? 8 : 0,
        border: dragging ? '2px dashed #2B95D6' : '2px dashed transparent',
        borderRadius: 4,
        transition: 'border-color 0.15s, padding 0.15s',
      }}
    >
      {dragging && (
        <div style={{ textAlign: 'center', color: '#2B95D6', fontSize: 12, padding: '4px 0' }}>
          Drop file{allowMultiple ? 's' : ''} here
        </div>
      )}

      {value.map((pointer) => (
        <div
          key={pointer.id}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          {signedUrls[pointer.id] ? (
            <a
              href={signedUrls[pointer.id]}
              target="_blank"
              rel="noopener noreferrer"
              onMouseEnter={() => prefetchSignedUrl(pointer)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap', fontSize: 13, textDecoration: 'none',
              }}
              title={pointer.filename}
            >
              {loadingUrlId === pointer.id ? <span className="sky-spinner" style={{ width: 12, height: 12, borderWidth: 2 }} /> : null}
              {pointer.filename}
            </a>
          ) : (
            <button
              className="sky-btn sky-btn-ghost"
              onClick={() => handleFileClick(pointer)}
              onMouseEnter={() => prefetchSignedUrl(pointer)}
              title={pointer.filename}
              style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', height: 24, fontSize: 12, padding: '0 8px' }}
            >{pointer.filename}</button>
          )}
          {!isReadOnly && (
            <button
              className="sky-icon-btn"
              disabled={!!deletingId}
              onClick={() => handleDelete(pointer)}
              title="Delete file"
              style={{ color: 'var(--red)' }}
            >
              {deletingId === pointer.id ? (
                <span className="sky-spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
              ) : (
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                </svg>
              )}
            </button>
          )}
        </div>
      ))}

      {!isReadOnly && canUpload && (
        <>
          <input
            ref={inputRef}
            type="file"
            multiple={allowMultiple}
            accept={allowedMimeTypes.length > 0 ? allowedMimeTypes.join(',') : undefined}
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <button
            className="sky-btn sky-btn-ghost"
            disabled={uploading || noRecord}
            title={noRecord ? 'Save the record before uploading files' : undefined}
            onClick={() => inputRef.current?.click()}
            style={{ height: 26, fontSize: 12, padding: '0 10px' }}
          >
            {uploading ? <span className="sky-spinner" style={{ width: 12, height: 12, borderWidth: 2 }} /> : 'Upload'}
          </button>
        </>
      )}
    </div>
  );
};

export default FileFieldInput;
